import { Link } from "@inertiajs/react";
import { ArrowRight } from "lucide-react";
import { motion } from "motion/react";

export default function ApparelHero({ hero }) {
  // 🛡️ Safe access with fallbacks
  const safeHero = hero || {};
  const image = safeHero.image || '';
  const eyebrow = safeHero.eyebrow || '';
  const title = safeHero.title || '';
  const subtitle = safeHero.subtitle || '';
  
  // Handle primary action with fallback
  const primaryAction = safeHero.primaryAction || {};
  const primaryLabel = primaryAction.label || safeHero.primaryLabel || 'Shop Now';
  const primaryHref = primaryAction.href || safeHero.primaryHref || '/shop';
  
  // Handle secondary action with fallback
  const secondaryAction = safeHero.secondaryAction || {};
  const secondaryLabel = secondaryAction.label || safeHero.secondaryLabel || 'Learn More';
  const secondaryHref = secondaryAction.href || safeHero.secondaryHref || '/about';

  // If no image, return a placeholder
  if (!image) {
    console.warn('ApparelHero: No image provided');
    return (
      <section className="relative isolate min-h-[620px] overflow-hidden rounded-[2rem] bg-neutral-100">
        <div className="flex min-h-[620px] items-center justify-center">
          <p className="text-neutral-500">Hero image not available</p>
        </div>
      </section>
    );
  }

  return (
    <section className="relative isolate min-h-[620px] overflow-hidden rounded-[2rem] bg-neutral-100 sm:min-h-[680px] lg:min-h-[720px]">
      <motion.img
        initial={{ scale: 1.05 }}
        animate={{ scale: 1 }}
        transition={{ duration: 1.2, ease: "easeOut" }}
        src={image}
        alt={safeHero.imageAlt || "Captex premium apparel editorial campaign"}
        className="absolute inset-0 size-full object-cover object-center"
      />

      <div className="absolute inset-0 bg-gradient-to-r from-white via-white/85 to-white/10 lg:via-white/60" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/15 via-transparent to-transparent" />

      <div className="relative flex min-h-[620px] items-end px-5 py-10 sm:min-h-[680px] sm:px-10 sm:py-14 lg:min-h-[720px] lg:items-center lg:px-16 xl:px-20">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="max-w-3xl"
        >
          {eyebrow && (
            <p className="text-xs font-black uppercase tracking-[0.35em] text-neutral-600">
              {eyebrow}
            </p>
          )}

          <h1 className="mt-5 max-w-4xl text-5xl font-black uppercase leading-[0.86] tracking-[-0.07em] text-neutral-950 sm:text-7xl md:text-8xl lg:text-[7rem] xl:text-[8.5rem]">
            {title || 'Collection'}
          </h1>

          {subtitle && (
            <p className="mt-7 max-w-xl text-sm leading-7 text-neutral-700 sm:text-base sm:leading-8 lg:text-lg">
              {subtitle}
            </p>
          )}

          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <Link
              href={primaryHref}
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-full bg-neutral-950 px-6 py-3 text-sm font-black uppercase text-white transition hover:-translate-y-0.5 hover:bg-neutral-800 focus:outline-none focus:ring-2 focus:ring-neutral-950 focus:ring-offset-2"
            >
              {primaryLabel}
              <ArrowRight className="size-4" />
            </Link>

            <Link
              href={secondaryHref}
              className="inline-flex min-h-12 items-center justify-center rounded-full border border-neutral-950 bg-white/80 px-6 py-3 text-sm font-black uppercase text-neutral-950 backdrop-blur transition hover:-translate-y-0.5 hover:bg-white focus:outline-none focus:ring-2 focus:ring-neutral-950 focus:ring-offset-2"
            >
              {secondaryLabel}
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
}